import {
  Avatar,
  Button,
  Dropdown,
  Image,
  TableColumnsType,
  Tooltip,
} from "antd";
import Header from "../../atoms/Header";
import TableUi from "../../atoms/Table";
import DashboardTemplate from "../../templates/Dashboard";
import {
  getCompanyInitials,
  getInitials,
  PAGE_LIMIT,
  useStableRandomColors,
} from "../../config/utils";
import Filters from "./Components/Filters";
import { useEffect, useRef, useState } from "react";
import { getCompanyListing } from "./ducks/actions";
import { useAppDispatch, useAppSelector } from "../../config/hooks";
import { CompanyListType } from "./ducks/types";
import { PopUp } from "../../atoms/PopUp";
import DeleteCompanyPopUp from "./Components/DeleteCompanyPopup";
import {
  changeCompanyStatus,
  deleteCompany,
  switchUserCompanyId,
  updateCompanyClientManager,
} from "./ducks/services";
import { AxiosError, AxiosResponse } from "axios";
import { ErrorMessage } from "../../atoms/Toasts/ErrorMessage";
import EmptyCard from "../../atoms/EmptyCard";
import { useNavigate } from "react-router-dom";
import ChangeClientManager from "./Components/ChangeClientManager";
import { CrossIcon, DeleteIcon, TickIcon } from "../../atoms/CustomIcons";
import { CaretDownOutlined, MoreOutlined } from "@ant-design/icons";
import ConfirmStatus from "./Components/ConfirmStatus";
import { SuccessMessage } from "../../atoms/Toasts/SuccessMessage";
import DeleteAllCompanyPopUp from "./Components/DeleteAllCompanyPopup";
import { formatRelativeTime } from "../../atoms/TimeFormatDisplay";
import dayjs from "dayjs";

export default function Companies() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const companyListing = useAppSelector((state) => state?.company?.companyData);
  const [pagination, setPagination] = useState(1);
  const [limit, setLimit] = useState(PAGE_LIMIT);
  const [tableRecord, setTableRecord] = useState<CompanyListType | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isAllDeleteModalOpen, setIsAllDeleteModalOpen] = useState(false);
  const [isClientModalOpen, setIsClientModalOpen] = useState(false);
  const [isSuspendedModalOpen, setIsSuspendedModalOpen] = useState(false);
  const [isArchivedModalOpen, setIsArchivedModalOpen] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<
    React.Key[] | string[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [archived, isArchived] = useState(false);
  const dropdownRef = useRef<HTMLButtonElement>(null);
  const avatarColor = useStableRandomColors(PAGE_LIMIT);
  const avatarColor2 = useStableRandomColors(PAGE_LIMIT);
  const [filters, setFilters] = useState<{
    client_manager: undefined | string[];
    status: undefined | string[];
    company_size: undefined | string[];
    state: undefined;
    city: undefined;
    last_user_activity: string[] | undefined;
    onboarding_complete_date: string[] | undefined;
    search_text: undefined | string;
  }>({
    client_manager: undefined,
    status: undefined,
    company_size: undefined,
    state: undefined,
    city: undefined,
    last_user_activity: undefined,
    onboarding_complete_date: undefined,
    search_text: undefined,
  });

  const fetchCompanyListing = () => {
    const payload = {
      offset: pagination > 1 ? limit * (pagination - 1) : pagination - 1,
      limit: limit,
      client_manager:
        filters?.client_manager && filters?.client_manager?.length > 0
          ? filters?.client_manager
          : undefined,
      status:
        filters?.status && filters?.status?.length > 0
          ? filters?.status
          : ["active", "onboarding", "inactive", "suspended"],
      company_size:
        filters?.company_size && filters?.company_size?.length > 0
          ? filters?.company_size
          : undefined,
      state: filters?.state ?? undefined,
      city: filters?.city ?? undefined,
      onboarding_complete_date: undefined,
      last_user_activity: undefined,
      search_text: undefined,
    };
    dispatch(getCompanyListing(payload));
  };

  useEffect(() => {
    const payload = {
      offset: pagination > 1 ? limit * (pagination - 1) : pagination - 1,
      limit: limit,
      client_manager:
        filters?.client_manager && filters?.client_manager?.length > 0
          ? filters?.client_manager
          : undefined,
      status:
        filters?.status && filters?.status?.length > 0
          ? filters?.status
          : ["active", "onboarding", "inactive", "suspended"],
      company_size:
        filters?.company_size && filters?.company_size?.length > 0
          ? filters?.company_size
          : undefined,
      state: filters?.state ?? undefined,
      city: filters?.city ?? undefined,
      onboarding_complete_date:
        filters?.onboarding_complete_date &&
        filters?.onboarding_complete_date?.[0] !== "" &&
        filters?.onboarding_complete_date?.[1] !== ""
          ? filters?.onboarding_complete_date
          : undefined,
      last_user_activity:
        filters?.last_user_activity &&
        filters?.last_user_activity?.[0] !== "" &&
        filters?.last_user_activity?.[1] !== ""
          ? filters?.last_user_activity
          : undefined,
      search_text: filters?.search_text || undefined,
    };
    dispatch(getCompanyListing(payload));
  }, [dispatch, filters, pagination, limit]);

  // Ensure current page is within available range after data changes (e.g., archiving/deleting items)
  useEffect(() => {
    const count = companyListing?.count ?? 0;
    const totalPages = Math.max(1, Math.ceil(count / limit));
    if (pagination > totalPages) {
      setPagination(totalPages);
    }
  }, [companyListing?.count, limit, pagination]);

  const changeStatus = (val: string) => {
    if (val === "suspended") {
      setIsSuspendedModalOpen(true);
      return;
    }
    if (val === "archived") {
      setIsArchivedModalOpen(true);
      return;
    }
    setLoading(true);
    functionChangeStatus(val);
  };

  const functionChangeStatus = (val: string) => {
    const payload = {
      status: val,
    };
    changeCompanyStatus(tableRecord?.id || "", payload)
      .then(() => {
        setLoading(false);
        fetchCompanyListing();
        setIsArchivedModalOpen(false);
        setIsSuspendedModalOpen(false);
        SuccessMessage(
          val === "archived"
            ? "Company archived"
            : val === "suspended"
            ? "Company suspended"
            : val === "active"
            ? "Company activated"
            : val === "inactive"
            ? "Company inactivated"
            : "Status updated"
        );
      })
      .catch((e: unknown) => {
        if (e instanceof AxiosError) {
          setLoading(false);
          ErrorMessage(
            e?.response?.data?.error?.message ?? "Something went wrong"
          );
        } else {
          setLoading(false);
          ErrorMessage("Something went wrong");
        }
      });
  };

  const items = [
    {
      key: "1",
      label: (
        <Button
          type="text"
          className="no-hover"
          icon={<Image src="/images/table/change-user.svg" preview={false} />}
          onClick={(e) => {
            e.stopPropagation();
            setIsClientModalOpen(true);
            setIsDeleteModalOpen(false);
            setIsSuspendedModalOpen(false);
            setIsArchivedModalOpen(false);
            if (dropdownRef.current) {
              dropdownRef?.current?.click(); // Simulate a click to close
            }
          }}
        >
          Change client manager
        </Button>
      ),
    },
    {
      key: "2",
      label: (
        <Button
          type="text"
          className="no-hover"
          icon={<Image src="/images/table/edit-icon.svg" preview={false} />}
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/edit-company/${tableRecord?.id}`);
          }}
        >
          Edit company details
        </Button>
      ),
    },
    {
      key: "3",
      label: (
        <Button
          type="text"
          className="delete-hover"
          icon={<DeleteIcon />}
          onClick={(e) => {
            e.stopPropagation();
            setIsDeleteModalOpen(true);
            setIsClientModalOpen(false);
            setIsSuspendedModalOpen(false);
            setIsArchivedModalOpen(false);
            if (dropdownRef.current) {
              dropdownRef?.current?.click(); // Simulate a click to close
            }
          }}
        >
          Delete
        </Button>
      ),
    },
  ];

  const statusItems = [
    {
      key: "header",
      label: (
        <div
          style={{
            padding: "6px 12px",
            color: "#474C4D",
            fontWeight: 500,
            fontSize: "12px",
            cursor: "default",
          }}
        >
          Change status to:
        </div>
      ),
      disabled: true,
    },
    {
      key: "inactive",
      label: (
        <Button
          type="text"
          className="no-hover"
          onClick={(e) => {
            e.stopPropagation();
            changeStatus("inactive");
            if (dropdownRef.current) {
              dropdownRef?.current?.click(); // Simulate a click to close
            }
          }}
        >
          <Avatar
            shape="square"
            size={16}
            style={{ backgroundColor: "#6C7478", borderRadius: "2px" }}
          />{" "}
          Inactive{" "}
          <TickIcon style={{ color: "#00A07D" }} className="status_checked" />
        </Button>
      ),
    },
    {
      key: "onboarding",
      label: (
        <Button
          type="text"
          className="no-hover"
          onClick={(e) => {
            e.stopPropagation();
            changeStatus("onboarding");
            if (dropdownRef.current) {
              dropdownRef?.current?.click(); // Simulate a click to close
            }
          }}
        >
          <Avatar
            shape="square"
            size={16}
            style={{ backgroundColor: "#2098EE", borderRadius: "2px" }}
          />{" "}
          Onboarding{" "}
          <TickIcon style={{ color: "#00A07D" }} className="status_checked" />
        </Button>
      ),
    },
    {
      key: "active",
      label: (
        <Button
          type="text"
          className="no-hover"
          onClick={(e) => {
            e.stopPropagation();
            changeStatus("active");
            if (dropdownRef.current) {
              dropdownRef?.current?.click(); // Simulate a click to close
            }
          }}
        >
          <Avatar
            shape="square"
            size={16}
            style={{ backgroundColor: "#00A07D", borderRadius: "2px" }}
          />{" "}
          Active{" "}
          <TickIcon style={{ color: "#00A07D" }} className="status_checked" />
        </Button>
      ),
    },
    {
      key: "suspended",
      label: (
        <Button
          type="text"
          className="no-hover"
          onClick={(e) => {
            e.stopPropagation();
            changeStatus("suspended");
            if (dropdownRef.current) {
              dropdownRef?.current?.click(); // Simulate a click to close
            }
          }}
        >
          <Avatar
            shape="square"
            size={16}
            style={{ backgroundColor: "#F06400", borderRadius: "2px" }}
          />{" "}
          Suspended{" "}
          <TickIcon style={{ color: "#00A07D" }} className="status_checked" />
        </Button>
      ),
    },
    {
      key: "archived",
      label: (
        <Button
          type="text"
          className="no-hover"
          onClick={(e) => {
            e.stopPropagation();
            changeStatus("archived");
            if (dropdownRef.current) {
              dropdownRef?.current?.click(); // Simulate a click to close
            }
          }}
        >
          <Avatar
            shape="square"
            size={16}
            style={{ backgroundColor: "#98A3A9", borderRadius: "2px" }}
          />{" "}
          Archived{" "}
          <TickIcon style={{ color: "#00A07D" }} className="status_checked" />
        </Button>
      ),
    },
  ];

  const columns: TableColumnsType<CompanyListType> = [
    {
      title: "Company name",
      dataIndex: "name",
      width: 216,
      fixed: "left",
      sorter: (a, b) => a?.name?.localeCompare(b?.name),
      render: (text, record, index) => {
        return (
          <div className="text_with_avatar">
            {record?.image_url ? (
              <div className="participant_avatar">
                <Avatar src={record?.image_url} />
              </div>
            ) : (
              <div className="participant_avatar">
                <Avatar
                  style={{ backgroundColor: avatarColor?.[index] ?? "#ccc" }}
                >
                  {getCompanyInitials(text)}
                </Avatar>
              </div>
            )}
            <div className="text_wrap">
              <Tooltip title={text}>
                {text}
                {text?.status && (
                  <span style={{ color: "#98A3A9" }}>({text?.status})</span>
                )}
              </Tooltip>
            </div>
          </div>
        );
      },
    },
    {
      title: "Client manager",
      dataIndex: "manager",
      width: 160,
      render: (text, _record, index) => {
        return text ? (
          <div className="text_with_avatar">
            {text?.image_url ? (
              <div className="participant_avatar">
                <Avatar src={text?.image_url} />
              </div>
            ) : (
              <div className="participant_avatar">
                <Avatar
                  style={{ backgroundColor: avatarColor2?.[index] ?? "#ccc" }}
                >
                  {getInitials(text?.first_name, text?.last_name)}
                </Avatar>
              </div>
            )}
            <div className="text_wrap">
              <Tooltip title={`${text?.first_name} ${text?.last_name}`}>
                {text?.first_name} {text?.last_name}{" "}
                {/* {text?.status && (
                <span style={{ color: "#98A3A9" }}>({text?.status})</span>
              )} */}
              </Tooltip>
            </div>
          </div>
        ) : (
          "-"
        );
      },
    },
    {
      title: "Company size",
      dataIndex: "company_size",
      width: 140,
      render: (text) =>
        text ? <div style={{ textTransform: "capitalize" }}>{text}</div> : "-",
    },
    {
      title: "Users",
      dataIndex: "user_count",
      width: 80,
      render: (text) => text ?? 0,
      align: "right",
    },
    {
      title: "Branches",
      dataIndex: "branches",
      width: 90,
      align: "right",
      render: (text) => text ?? "-",
    },
    {
      title: "City",
      dataIndex: "city",
      width: 170,
      sorter: (a, b) => a?.city?.localeCompare(b?.city),
      render: (text: string) => (
        <Tooltip title={text ? text : "-"}>
          <span className="table-col-ellipsis" style={{ width: "160px" }}>
            {text ? text : "-"}
          </span>
        </Tooltip>
      ),
    },
    {
      title: "State",
      dataIndex: "state",
      width: 80,
      sorter: (a, b) => a?.state?.localeCompare(b?.state),
      render: (text: string) => (
        <Tooltip title={text ? text : "-"}>
          <span className="table-col-ellipsis" style={{ width: "70px" }}>
            {text ? text : "-"}
          </span>
        </Tooltip>
      ),
    },
    {
      title: "Created",
      dataIndex: "created_date",
      width: 130,
      render: (text) => (text ? dayjs(text).format("MMM D, YYYY") : "-"),
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 142,
      render: (text, record) => (
        <Dropdown
          menu={{
            items: statusItems,
            selectable: true,
            selectedKeys: [text],
          }}
          trigger={["click"]}
          overlayClassName="status_dropdown_overlay no-hover-color"
        >
          <Button
            onClick={(e) => {
              setTableRecord(record);
              e.stopPropagation();
            }}
            onKeyDown={(e) => e.stopPropagation()}
            className={`status_dropdown ${text}_status`}
            ref={dropdownRef}
            style={{ textTransform: "capitalize" }}
          >
            {text}{" "}
            <div className="icon_arrow">
              <CaretDownOutlined />
            </div>
          </Button>
        </Dropdown>
      ),
    },
    {
      title: "Onboarding completed",
      dataIndex: "onboarding_complete_date",
      width: 230,
      render: (text) => (text ? dayjs(text).format("MMM D, YYYY") : "-"),
    },
    {
      title: "Last user activity",
      dataIndex: "last_user_activity",
      width: 210,
      render: (text) => (text ? formatRelativeTime(text) : "-"),
    },
    {
      title: <Image src="/images/table/menu-burger.svg" preview={false} />,
      dataIndex: "action",
      width: 52,
      fixed: "right",
      render: (_text, record) => (
        <Dropdown
          menu={{ items }}
          trigger={["click"]}
          placement="bottomRight"
          className="actionButton"
        >
          <Button
            onClick={(e) => {
              console.log("text", record);
              setTableRecord(record);
              e.stopPropagation();
            }}
            onKeyDown={(e) => e.stopPropagation()}
            style={{
              padding: "6px",
              height: "auto",
              background: "transparent",
            }}
            className="only_icon"
            icon={<MoreOutlined />}
            ref={dropdownRef}
          />
        </Dropdown>
      ),
    },
  ];

  const deleteCompanyPopUp = {
    visibility: isDeleteModalOpen,
    content: (
      <DeleteCompanyPopUp
        tableRecord={tableRecord}
        onClose={() => setIsDeleteModalOpen(false)}
        apiSubmit={() => apiSubmitDeleteCompany()}
        loading={loading}
      />
    ),
    width: 480,
    onCancel: () => setIsDeleteModalOpen(false),
  };

  const deleteAllCompanyPopUp = {
    visibility: isAllDeleteModalOpen,
    content: (
      <DeleteAllCompanyPopUp
        onClose={() => setIsAllDeleteModalOpen(false)}
        apiSubmit={() => apiSubmitDeleteCompany()}
        loading={loading}
      />
    ),
    width: 480,
    onCancel: () => setIsAllDeleteModalOpen(false),
  };

  const changeClientManagerPopUp = {
    visibility: isClientModalOpen,
    content: (
      <ChangeClientManager
        tableRecord={tableRecord}
        onClose={() => setIsClientModalOpen(false)}
        apiSubmit={(e: string) => apiSubmitClientManager(e)}
        loading={loading}
      />
    ),
    width: 480,
    onCancel: () => setIsClientModalOpen(false),
  };

  const confirmSuspendedPopUp = {
    visibility: isSuspendedModalOpen,
    content: (
      <ConfirmStatus
        onClose={() => setIsSuspendedModalOpen(false)}
        apiSubmit={() => functionChangeStatus("suspended")}
        loading={loading}
        btnText="Suspend company account"
        btnClass="mint-btn"
        title="Company users’ will lose access"
        description={
          <>
            All users of the suspended company will no longer be able to access
            their accounts or receive notifications.
            <br />
            <br /> You will still be able to view and manage the company while
            it's suspended.
            <br />
            <br /> Are you sure you want to suspend <b> {tableRecord?.name}</b>?
          </>
        }
      />
    ),
    width: 480,
    onCancel: () => setIsSuspendedModalOpen(false),
  };

  const confirmArchivedPopUp = {
    visibility: isArchivedModalOpen,
    content: (
      <ConfirmStatus
        onClose={() => setIsArchivedModalOpen(false)}
        apiSubmit={() => functionChangeStatus("archived")}
        loading={loading}
        btnText="Archive company"
        btnClass="danger-btn"
        title="Company users’ will lose access"
        description={
          <>
            All users of the archived company will no longer be able to access
            their accounts and will stop receiving notifications. The company
            will be moved to “Archived” list.
            <br />
            <br /> You will still be able to view and manage the company, and
            reactivate it if needed.
            <br />
            <br /> Are you sure you want to archive <b> {tableRecord?.name}</b>?
          </>
        }
      />
    ),
    width: 480,
    onCancel: () => setIsArchivedModalOpen(false),
  };

  const apiSubmitDeleteCompany = () => {
    setLoading(true);
    deleteCompany(
      selectedRowKeys?.length > 0 ? selectedRowKeys : [tableRecord?.id || ""]
    )
      .then(() => {
        setLoading(false);
        setIsDeleteModalOpen(false);
        setIsAllDeleteModalOpen(false);
        setSelectedRowKeys([]);
        fetchCompanyListing();
        SuccessMessage("Company deleted");
      })
      .catch((e: unknown) => {
        if (e instanceof AxiosError) {
          setLoading(false);
          ErrorMessage(
            e?.response?.data?.error?.message ?? "Something went wrong"
          );
        } else {
          setLoading(false);
          ErrorMessage("Something went wrong");
        }
      });
  };

  const apiSubmitClientManager = (val: string) => {
    const payload = {
      client_manager: val ?? "",
    };
    setLoading(true);
    setIsClientModalOpen(false);
    updateCompanyClientManager(tableRecord?.id || "", payload)
      .then(() => {
        setLoading(false);
        fetchCompanyListing();
        SuccessMessage(
          val ? "Client manager assigned" : "Client manager unassigned."
        );
      })
      .catch((e: unknown) => {
        if (e instanceof AxiosError) {
          setLoading(false);
          ErrorMessage(
            e?.response?.data?.error?.message ?? "Something went wrong"
          );
        } else {
          setLoading(false);
          ErrorMessage("Something went wrong");
        }
      });
  };

  const onClickRow = (record: CompanyListType) => {
    return {
      onClick: () => {
        switchUserCompanyId(record?.id || "")
          .then((res: AxiosResponse) => {
            if (res?.data?.accessToken) {
              const userData = localStorage.getItem("@lsToken");
              const parsedData = userData ? JSON.parse(userData) : null;
              if (parsedData) {
                const updatedData = {
                  ...parsedData,
                  accessToken: res?.data?.accessToken,
                };
                localStorage.setItem("@lsToken", JSON.stringify(updatedData));
              }

              navigate(`/company-settings/${record?.id}`);
            }
          })
          .catch((e: unknown) => {
            if (e instanceof AxiosError) {
              setLoading(false);
              ErrorMessage(
                e?.response?.data?.error?.message ?? "Something went wrong"
              );
            } else {
              setLoading(false);
              ErrorMessage("Something went wrong");
            }
          });
      },
    };
  };
  console.log("selectedRowKeys", selectedRowKeys);

  return (
    <DashboardTemplate loading={companyListing?.loader || loading}>
      <Header
        title="Companies"
        description="The list of all LandScaling customers."
      />

      <TableUi<CompanyListType>
        columns={columns}
        data={{
          count: companyListing?.count,
          data: companyListing?.data || [],
        }}
        filters={
          <Filters
            setFilters={setFilters}
            filters={filters}
            archived={archived}
            isArchived={isArchived}
          />
        }
        pageSize={limit}
        setPageSize={setLimit}
        setCurrentPage={setPagination}
        currentPage={pagination}
        selectedRowKeys={selectedRowKeys}
        setSelectedRowKeys={setSelectedRowKeys}
        createButtonText="Create company"
        createButtonLink="/create-company"
        onClickRow={onClickRow}
      />
      {companyListing?.count === 0 && (
        <div className="empty_wrap">
          <EmptyCard
            imageHeight={220}
            title={
              archived
                ? "There are no archived companies"
                : filters?.search_text
                ? "No results match the filter or search criteria"
                : `There are no ${filters?.status ?? "active"} companies`
            }
            description={
              archived
                ? "We believe in our absolute success and hope to never have an archived company here."
                : filters?.search_text
                ? "Try adjusting the filters and search."
                : ""
            }
            image={
              archived
                ? "/images/empty-icons/no-archived.svg"
                : "/images/empty-icons/no-active.svg"
            }
          />
        </div>
      )}

      {selectedRowKeys?.length !== 0 ? (
        <div className="selection_bar">
          <div className="selected_count">
            <Button
              icon={<CrossIcon style={{ color: "white" }} />}
              className="only_icon"
              type="text"
              onClick={() => setSelectedRowKeys([])}
            />
            {selectedRowKeys.length}{" "}
            {`${selectedRowKeys.length === 1 ? "company" : "companies"}`}{" "}
            selected
          </div>
          <div className="selection_buttons">
            <Button
              type="text"
              icon={<DeleteIcon />}
              onClick={() => {
                setIsAllDeleteModalOpen(true);
              }}
            >
              Delete
            </Button>
          </div>
        </div>
      ) : null}

      <PopUp {...deleteCompanyPopUp} />
      <PopUp {...deleteAllCompanyPopUp} />
      <PopUp {...changeClientManagerPopUp} />
      <PopUp {...confirmSuspendedPopUp} />
      <PopUp {...confirmArchivedPopUp} />
    </DashboardTemplate>
  );
}
